import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useTags, useCreateTag } from "@/hooks/structural/useTags";
import { X, Plus, Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface DealTagsFieldProps {
  selectedTagIds: string[];
  onTagsChange: (tagIds: string[]) => void;
}

const TAG_COLORS = [
  "#ef4444", // red
  "#f97316", // orange
  "#f59e0b", // amber
  "#eab308", // yellow
  "#84cc16", // lime
  "#22c55e", // green
  "#10b981", // emerald
  "#14b8a6", // teal
  "#06b6d4", // cyan
  "#0ea5e9", // sky
  "#3b82f6", // blue
  "#6366f1", // indigo
  "#8b5cf6", // violet
  "#8f11c1", // purple - RGB(143, 17, 193)
  "#d946ef", // fuchsia
  "#ec4899", // pink
];

export function DealTagsField({ selectedTagIds, onTagsChange }: DealTagsFieldProps) {
  const { data: tags = [] } = useTags();
  const createTagMutation = useCreateTag();
  const [isCreating, setIsCreating] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState(TAG_COLORS[0]);

  const selectedTags = tags.filter((tag) => selectedTagIds.includes(tag.id));
  const availableTags = tags.filter((tag) => !selectedTagIds.includes(tag.id));

  const handleToggleTag = (tagId: string) => {
    if (selectedTagIds.includes(tagId)) {
      onTagsChange(selectedTagIds.filter((id) => id !== tagId));
    } else {
      onTagsChange([...selectedTagIds, tagId]);
    }
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;

    try {
      const newTag = await createTagMutation.mutateAsync({
        name: newTagName.trim(),
        color: newTagColor,
      });
      onTagsChange([...selectedTagIds, newTag.id]);
      setNewTagName("");
      setNewTagColor(TAG_COLORS[0]);
      setIsCreating(false);
    } catch (error) {
      console.error("Error creating tag:", error);
    }
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">Tags</label>
      
      {/* Selected Tags */}
      <div className="flex flex-wrap gap-2 min-h-[40px] p-2 border rounded-md bg-background">
        {selectedTags.length === 0 ? (
          <span className="text-sm text-muted-foreground">Nenhuma tag selecionada</span>
        ) : (
          selectedTags.map((tag) => (
            <Badge
              key={tag.id}
              className="text-xs px-2 py-1 gap-1"
              style={{
                backgroundColor: tag.color,
                color: '#ffffff',
                border: 'none'
              }}
            >
              {tag.name}
              <button
                type="button"
                onClick={() => handleToggleTag(tag.id)}
                className="ml-1 hover:bg-white/20 rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))
        )}
      </div>

      {/* Add Tags Button */}
      <Popover>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" size="sm" className="w-full">
            <Plus className="h-4 w-4 mr-2" />
            Adicionar Tags
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-4" align="start">
          <div className="space-y-3">
            <h4 className="font-medium text-sm">Selecionar Tags</h4>
            
            {/* Available Tags */}
            {availableTags.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">Tags disponíveis:</p>
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                  {availableTags.map((tag) => (
                    <Badge
                      key={tag.id}
                      className="cursor-pointer hover:opacity-80 text-xs px-2 py-1"
                      style={{
                        backgroundColor: tag.color,
                        color: '#ffffff',
                        border: 'none'
                      }}
                      onClick={() => handleToggleTag(tag.id)}
                    >
                      {tag.name}
                      <Plus className="h-3 w-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Create New Tag */}
            <div className="pt-3 border-t space-y-3">
              {!isCreating ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsCreating(true)}
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Criar Nova Tag
                </Button>
              ) : (
                <div className="space-y-3">
                  <Input
                    placeholder="Nome da tag..."
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleCreateTag();
                      }
                    }}
                  />
                  
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">Escolha uma cor:</p>
                    <div className="grid grid-cols-8 gap-2">
                      {TAG_COLORS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          className={cn(
                            "w-6 h-6 rounded-full border-2 transition-all",
                            newTagColor === color ? "border-foreground scale-110" : "border-transparent"
                          )}
                          style={{ backgroundColor: color }}
                          onClick={() => setNewTagColor(color)}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleCreateTag}
                      disabled={!newTagName.trim() || createTagMutation.isPending}
                      className="flex-1"
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Criar
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setIsCreating(false);
                        setNewTagName("");
                        setNewTagColor(TAG_COLORS[0]);
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
